import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Disclosure, Transition } from "@headlessui/react";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

const Bot = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onTouched" });

  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Welcome to Ayushmana by IDC India! Ask me anything about FMGE topics. Let’s crack this together!",
      image: null,
    },
  ]);

  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [currentFeedbackContext, setCurrentFeedbackContext] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const isMobile = useIsMobile();

  if (isMobile === null) return null;

  const defaultOpen = !isMobile;

  const onSubmit = async (data) => {
    const userMessage = data.message;

    setMessages((prev) => [...prev, { type: "user", text: userMessage }]);

    const typingMessage = { type: "bot", text: "Analyzing your question..." };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BOT_API_URL}/ask`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      const json = await response.json();

      setMessages((prev) => prev.slice(0, -1));

      if (json.success) {
        setIsSuccess(true);
        setStatusMsg(json.message);
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: json.message, image: json.image || null },
        ]);
      } else {
        setIsSuccess(false);
        setStatusMsg(json.message);
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: "Oops! Something went wrong." },
        ]);
      }
    } catch (error) {
      setMessages((prev) => prev.slice(0, -1));

      setIsSuccess(false);
      setStatusMsg("Client Error");
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: "There was a technical issue. Try again later." },
      ]);
    } finally {
      reset();
    }
  };

  const handleThumbsDown = (userQuestion, botResponse) => {
    setCurrentFeedbackContext({ question: userQuestion, answer: botResponse });
    setShowFeedbackPrompt(true);
  };

  const submitFeedback = async () => {
    if (!currentFeedbackContext) return;

    await fetch(`${process.env.REACT_APP_BOT_API_URL}/bot/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question: currentFeedbackContext.question,
        answer: currentFeedbackContext.answer,
        feedback: feedbackMessage,
      }),
    });

    setShowFeedbackPrompt(false);
    setFeedbackMessage("");
  };

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <Disclosure.Button className="fixed bottom-[90px] right-4 z-50 w-16 h-16 flex items-center justify-center bg-[#E02424] hover:bg-red-600 rounded-full shadow-lg transition focus:outline-none">
            <span className="sr-only">Toggle Chatbot</span>
            {open ? (
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <span className="text-white font-bold text-lg">AI</span>
            )}
          </Disclosure.Button>

          <Transition
            className="fixed bottom-[145px] sm:right-5 left-0 right-0 sm:left-auto sm:w-[350px] z-50"
            enter="transition duration-200 transform ease"
            enterFrom="opacity-0 translate-y-5"
            leave="transition duration-200 transform ease"
            leaveTo="opacity-0 translate-y-5"
          >
            <Disclosure.Panel className="bg-white border rounded-lg shadow-xl flex flex-col h-[500px] overflow-hidden">
              <div className="bg-[#E02424] p-4 text-white">
                <h2 className="text-lg font-semibold text-center">
                  AI Assistant
                </h2>
                <p className="text-sm opacity-80 text-center">
                  Ask me anything!
                </p>
              </div>

              <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`max-w-[80%] px-4 py-2 rounded-lg text-sm ${
                      msg.type === "bot"
                        ? "bg-gray-200 text-gray-800 self-start"
                        : "bg-[#E02424] text-white self-end ml-auto"
                    }`}
                  >
                    <div dangerouslySetInnerHTML={{ __html: msg.text }} />

                    {msg.image && (
                      <img
                        src={msg.image}
                        alt="Bot Explanation"
                        className="mt-2 rounded max-w-full h-auto"
                      />
                    )}
                    {msg.type === "bot" &&
                      idx > 0 &&
                      msg.text !== "Analyzing your question..." && (
                        <div className="flex items-center gap-2 mt-1 text-sm">
                          <button
                            onClick={() =>
                              handleThumbsDown(
                                messages.find(
                                  (m, i) => i < idx && m.type === "user"
                                )?.text || "",
                                msg.text
                              )
                            }
                            className="hover:text-red-600 transition"
                            title="Not helpful"
                          >
                            👎
                          </button>

                          <button
                            className="hover:text-green-600 transition"
                            title="Helpful"
                          >
                            👍
                          </button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="p-3 border-t bg-white flex items-center gap-2"
              >
                <input
                  type="text"
                  placeholder="Type your message..."
                  {...register("message", {
                    required: "Message is required",
                    minLength: { value: 2, message: "Too short" },
                  })}
                  className="flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E02424]"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#E02424] hover:bg-[#c81d1d] text-white px-4 py-2 rounded-md text-sm"
                >
                  {isSubmitting ? "..." : "Send"}
                </button>
                {showFeedbackPrompt && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-4 rounded shadow-md w-80">
                      <h3 className="text-md font-semibold mb-2 text-gray-800">
                        We’re sorry if it wasn’t helpful.
                      </h3>
                      <textarea
                        className="w-full border rounded p-2 text-sm"
                        rows={3}
                        placeholder="Tell us what went wrong..."
                        value={feedbackMessage}
                        onChange={(e) => setFeedbackMessage(e.target.value)}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={() => setShowFeedbackPrompt(false)}
                          className="text-sm px-3 py-1 border rounded"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitFeedback}
                          className="text-sm px-3 py-1 bg-[#E02424] text-white rounded"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
};

export default Bot;
